// Changing a Calendar board: each change one step that Undo takes back, saved as entries.
// Times the user moves on screen are on their own clock; an event kept on another zone's clock is moved to the same
// moment there, so it stays where it was dropped for everyone.

import { DEFAULT_MINUTES, calSetId, calSetOf, cleanEvent, eventOf, hereZone, localAt, localMinutes, momentOf, quickEvent, shownOf, type CalSetData, type EventData, type QuickEvent } from "../../../shared/agenda";
import { newId } from "../../../shared/ids";
import type { Entry, ID } from "../../../shared/types";
import { commit, putEntries } from "../../state/actions";
import { flush } from "../../state/sync";
import { boardEntries, get, glowItems, toast } from "../../state/store";
import { setAt, today } from "./state";
import { dayFirst } from "./region";

/** The zone new events are kept in: the browser's. */
export const zoneHere = () => hereZone() ?? "UTC";

/** An event's length in real minutes, whatever the clocks do between its start and end. */
export function lengthOf(ev: EventData): number {
  if (ev.allDay) return 0;
  return Math.round((momentOf(ev.end, ev.tz) - momentOf(ev.start, ev.tz)) / 60_000);
}

/** A timed event's times when it starts at a time on the viewer's clock and lasts minutes, on its own zone's clock. */
export function timesAt(ev: Pick<EventData, "tz">, startHere: string, minutes: number): { start: string; end: string } {
  const t = momentOf(startHere, zoneHere());
  return { start: localAt(t, ev.tz), end: localAt(t + minutes * 60_000, ev.tz) };
}

/** A new event on the board, from what the user made or typed; it glows once where it lands. history false: no step of its own on Undo (the caller records one). */
export function addEvent(boardId: ID, data: Omit<EventData, "tz"> & { tz?: string | null }, history = true): Entry | null {
  const now = Date.now();
  let clean: EventData;
  try {
    clean = cleanEvent({ ...data, tz: data.allDay ? null : (data.tz ?? zoneHere()) }, null);
  } catch (e) {
    toast({ level: "warn", title: "That event could not be made", body: e instanceof Error ? e.message : null });
    return null;
  }
  const entry: Entry = { id: newId("e_"), boardId, t: "event", parent: null, order: 0, data: { ...clean }, createdAt: now, updatedAt: now, by: null };
  if (history) putEntries([entry], "Add event");
  else commit([{ op: "entry.put", entry }], "Add event", { history: false });
  setTimeout(() => glowItems([entry.id]), 60);
  return entry;
}

/** Changes an event's fields in one step; a change the cleaner refuses says why and changes nothing. */
export function patchEvent(id: ID, patch: Partial<Omit<EventData, "location" | "notes" | "color">> & { location?: string | null; notes?: string | null; color?: EventData["color"] | null }, label = "Edit event"): boolean {
  const e = get().entries[id];
  if (!e || e.t !== "event") return false;
  const ev = eventOf(e);
  let next: EventData;
  try {
    next = cleanEvent({ ...ev, ...patch }, null);
  } catch (err) {
    toast({ level: "warn", title: "That change was not made", body: err instanceof Error ? err.message : null });
    return false;
  }
  if (JSON.stringify(next) === JSON.stringify(ev)) return false;
  putEntries([{ ...e, data: { ...next } }], label);
  return true;
}

/** A timed event moved to start at a time on the viewer's clock, lasting as long as it did. */
export function moveTo(id: ID, startHere: string) {
  const e = get().entries[id];
  if (!e || e.t !== "event") return;
  const ev = eventOf(e);
  if (ev.allDay) return;
  patchEvent(id, timesAt(ev, startHere, lengthOf(ev)), "Move event");
}

/** A timed event's end moved to a time on the viewer's clock; it lasts at least 15 minutes. */
export function endAt(id: ID, endHere: string) {
  const e = get().entries[id];
  if (!e || e.t !== "event") return;
  const ev = eventOf(e);
  if (ev.allDay) return;
  const s = shownOf(e, zoneHere());
  const minutes = Math.max(15, localMinutes(s.start, endHere));
  patchEvent(id, timesAt(ev, s.start, minutes), "Change event length");
}

/** Between all day and a time: all day keeps its days; a time starts at 9 on its first day, for an hour. */
export function setAllDay(id: ID, allDay: boolean) {
  const e = get().entries[id];
  if (!e || e.t !== "event") return;
  const ev = eventOf(e);
  if (ev.allDay === allDay) return;
  if (allDay) {
    const s = shownOf(e, zoneHere());
    patchEvent(id, { allDay: true, start: s.first, end: s.last, tz: null }, "Make it all day");
  } else patchEvent(id, { allDay: false, start: `${ev.start}T09:00`, end: `${ev.start}T${String(9 + DEFAULT_MINUTES / 60).padStart(2, "0")}:00`, tz: zoneHere() }, "Give it a time");
}

/**
 * The quick add: an event as typed ("Lunch with Sam Friday 1pm 1h"), on the day it names, else today when today is in
 * view, else the first day in view. The view goes to it, and it glows there. Words for the box when nothing was made.
 */
export function quickAdd(boardId: ID, text: string, inView: [string, string], over?: Pick<QuickEvent, "allDay" | "start" | "end">): string | null {
  const t = today();
  const fallback = t >= inView[0] && t <= inView[1] ? t : inView[0];
  const q = quickEvent(text, t, fallback, { dmy: dayFirst });
  if (!q) return "Type what it is and when, like Lunch with Sam Friday 1pm.";
  // what was picked on the chips is what was meant, over what the words said
  Object.assign(q, over ?? {});
  // on the board, and in the Google calendars the box names (the board's choice), each written there first (edit.ts)
  void Promise.all([import("./edit"), import("./copies")]).then(([m, c]) => c.createIn(boardId, m.defaultCalendars(boardId), { title: q.title, allDay: q.allDay, start: q.start, end: q.end, ...(q.repeat ? { repeat: q.repeat } : {}) }));
  const day = q.start.slice(0, 10);
  if (day < inView[0] || day > inView[1]) setAt(boardId, day);
  return null;
}

/** The board's settings, kept in its one settings entry (made the first time one changes). */
function putSet(boardId: ID, data: CalSetData, label: string, history = true) {
  const id = calSetId(boardId);
  const was = get().entries[id];
  const now = Date.now();
  const entry: Entry = was ? { ...was, data: { ...data } } : { id, boardId, t: "calset", parent: null, order: 0, data: { ...data }, createdAt: now, updatedAt: now, by: null };
  commit([{ op: "entry.put", entry }], label, { history });
}

/** The server reads the calendars a board shows: once a change to them is sent, every board reads its Google events again. */
function reread(key: string) {
  if (key.startsWith("g:")) void flush().then(() => import("../../state/gcal").then((m) => m.gcalChanged()), () => null);
}

/**
 * Ticks or unticks a calendar (or a layer) on this board, for everyone who opens it: an unticked one is hidden by naming
 * it, and stays in the board's list to be ticked again.
 */
export function showCalendar(boardId: ID, key: string, on: boolean) {
  showCalendars(boardId, [key], on, on ? "Show a calendar" : "Hide a calendar");
}

/** Ticks or unticks several at once, one step for Undo: a Google account's calendars, from its row in the column. */
export function showCalendars(boardId: ID, keys: string[], on: boolean, label = on ? "Show an account" : "Hide an account") {
  const set = calSetOf(boardEntries(get(), boardId), boardId);
  const hidden = on ? set.hidden.filter((k) => !keys.includes(k)) : [...new Set([...set.hidden, ...keys])];
  if (hidden.length === set.hidden.length && hidden.every((k) => set.hidden.includes(k))) return;
  putSet(boardId, { ...set, hidden }, label);
  const google = keys.find((k) => k.startsWith("g:"));
  if (google) reread(google);
}

/**
 * Puts a Google calendar in this board's list, or takes it out (the switch on its account's page), for this board
 * only: it stays connected and every other Calendar board keeps it. Put back, it is ticked. selectedInGoogle: the list
 * starts from the calendars Google shows, so one of those is taken out by naming it, any other added by naming it.
 */
export function listCalendar(boardId: ID, key: string, inList: boolean, selectedInGoogle: boolean) {
  const set = calSetOf(boardEntries(get(), boardId), boardId);
  const without = (list: string[] | undefined) => (list ?? []).filter((k) => k !== key);
  const withKey = (list: string[] | undefined) => [...new Set([...(list ?? []), key])];
  putSet(boardId, { ...set, hidden: without(set.hidden), shown: selectedInGoogle ? set.shown : inList ? withKey(set.shown) : without(set.shown), out: selectedInGoogle ? (inList ? without(set.out) : withKey(set.out)) : set.out }, inList ? "Add a calendar to this board" : "Take a calendar off this board's list");
  reread(key);
}

/** Puts a Google calendar in this board's list or takes it out, for every copy of it (a calendar two connected accounts share). */
export function listCalendars(boardId: ID, copies: { key: string; selected: boolean }[], inList: boolean) {
  for (const c of copies) listCalendar(boardId, c.key, inList, c.selected);
}

/** Gives a calendar a color of its own (a theme color's name, or "#rrggbb"), or Google's again with none; every Calendar board and the calendar widget follow. */
export function setCalColor(key: string, pick: string | null) {
  setCalColors({ [key]: pick });
}

/** Gives several calendars their colors in one step; null puts a calendar's back to Google's. */
export function setCalColors(picks: Record<string, string | null>) {
  const next = { ...(get().workspace?.calColors ?? {}) };
  for (const [k, v] of Object.entries(picks)) if (v) next[k] = v;
  else delete next[k];
  commit([{ op: "workspace.put", patch: { calColors: Object.keys(next).length ? next : null } }], "Calendar colors", { history: false });
}

/**
 * The Google calendars new events go into on this board, for everyone who opens it (every event is on the board too).
 * quiet: picked for one new event (in its card or the quick add), which the next one starts on too; a choice like the
 * view's, so no step of its own on Undo.
 */
export function setDefaultCalendars(boardId: ID, keys: string[], quiet = false) {
  const set = calSetOf(boardEntries(get(), boardId), boardId);
  const into = [...new Set(keys)];
  if (into.length === (set.into ?? []).length && into.every((k) => set.into?.includes(k))) return;
  putSet(boardId, { ...set, ...(set.shown?.length ? {} : { shown: undefined }), into }, "Choose where new events go", !quiet);
}
